import * as React from "react";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { TextField, CircularProgress } from "@material-ui/core";
import { useAsyncCallback } from "react-async-hook";
import { listPartTwoPrompt, createPartTwo } from "utilities/awsCRUD";
import PartTwoCreationDialog from "./PartTwoCreationDialog";
import { useSnackbar } from "notistack";
import { isObjFieldFull } from "utilities/validation";
import { setOriginalContext } from "routes/Practice/speaking/SpeakingCreationView";

interface TopicDropDownProps {
  speaking: any;
  updateSpeaking: (data: any) => void;
}

const initialDialogValue = {
  prompt: "",
  subQuestions: ["", "", "", ""],
  season: [],
  type: "",
} as any;

const filter = createFilterOptions<any>();
const handleOptionLabel = (option: any) =>
  typeof option === "string" ? option : option.prompt;
const handleFilterOptions = (options: any, params: any) => {
  const filtered = filter(options, params) as any;
  if (params.inputValue !== "") {
    filtered.push({
      inputValue: params.inputValue,
      prompt: `Add "${params.inputValue}"`,
    });
  }
  return filtered;
};

const TopicDropDown: React.FC<TopicDropDownProps> = ({
  speaking,
  updateSpeaking,
}) => {
  const { fetchSpeaking } = React.useContext(setOriginalContext);
  const { typeName } = speaking;
  const { enqueueSnackbar } = useSnackbar();
  const asyncOptions = useAsyncCallback(listPartTwoPrompt);
  const asyncCreatePartTwo = useAsyncCallback(createPartTwo);
  const value = speaking.id ? speaking : null;
  const [options, setOptions] = React.useState<any[]>([]);
  const [dialogValue, setDialogValue] = React.useState<any>(initialDialogValue);
  const [open, setOpen] = React.useState<boolean>(false);
  const onValueChange = async (_event: any, newValue: any) => {
    if (typeof newValue === "string" || newValue?.inputValue) {
      const prompt =
        typeof newValue === "string" ? newValue : newValue.inputValue;
      setDialogValue({ ...dialogValue, prompt });
      setOpen(true);
    } else if (newValue === null) {
      updateSpeaking({ typeName });
    } else {
      fetchSpeaking(newValue.id, "PartTwo");
    }
  };
  const onOpen = (_event: any) => {
    asyncOptions.execute();
  };
  const onClose = () => {
    setOpen(false);
    setDialogValue(initialDialogValue);
  };
  const onSubmit = () => {
    if (isObjFieldFull(dialogValue)) {
      asyncCreatePartTwo.execute(dialogValue);
    } else {
      enqueueSnackbar("You are missing fields", { variant: "error" });
    }
  };
  React.useEffect(() => {
    if (asyncOptions.result) {
      setOptions(asyncOptions.result);
    }
  }, [asyncOptions.result, setOptions]);

  React.useEffect(() => {
    const result = asyncCreatePartTwo.result;
    if (result) {
      setOptions((options) => [...options, result]);
      fetchSpeaking(result.id, "PartTwo");
      onClose();
    }
    //eslint-disable-next-line
  }, [asyncCreatePartTwo.result, setOptions]);
  return (
    <>
      <Autocomplete
        onOpen={onOpen}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        autoComplete
        freeSolo
        options={options}
        getOptionLabel={handleOptionLabel}
        filterOptions={handleFilterOptions}
        value={value}
        onChange={onValueChange}
        style={{ minWidth: 300, maxWidth: 600 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Part Two Prompt"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {asyncOptions.loading ? (
                    <CircularProgress color="primary" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {open && (
        <PartTwoCreationDialog
          dialogValue={dialogValue}
          setDialogValue={setDialogValue}
          open={open}
          onClose={onClose}
          onSubmit={onSubmit}
          loading={asyncCreatePartTwo.loading}
        />
      )}
    </>
  );
};

export default TopicDropDown;
